import React, { useCallback, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Switch,
  Divider,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getUser, isAuthorized } from "../../helpers/sessionStorageHelper";
import { isApiPending } from "../../dux/apiStatus";
import {
  fetchPaymentReconciliationReport,
  runPaymentReconciliation,
  fetchPaymentReconciliationBookingDetail,
  clearPaymentReconciliationError,
  clearPaymentReconciliationBookingDetail,
  selectPaymentReconciliationScan,
  selectPaymentReconciliationError,
  selectPaymentReconciliationBookingDetail,
  selectPaymentReconciliationBookingDetailError,
} from "../../dux/paymentReconciliation";
import { openSnackbar } from "../../dux/snackbar";
import {
  formatInrFromMinor,
  formatIsoForDisplay,
  formatSlotDisplay,
  formatTimelineEventLabel,
  getIssueKindLabel,
  getPrimaryGatewayStatusDisplay,
  getPrimaryGatewayStatusTooltip,
  getRazorpayPaymentStatusDisplay,
  getTimelineSteps,
  isReconciliationOrphanIssue,
  sortLifecycleEventsAsc,
} from "../../helpers/paymentReconciliationHelper";
import { snackBarStatus } from "../../helpers/constants";
import styles from "./PaymentReconciliationPage.module.css";

const BOOKING_STATUS_OPTIONS = [
  "IN_PROGRESS",
  "PAYMENT_PENDING",
  "PAYMENT_FAILED",
  "CONFIRMED",
  "COMPLETED",
  "CANCELLED",
];

function getAllowedLocations() {
  const user = getUser();
  return user && user.locations ? user.locations : [];
}

const locationDisplay = {
  srinagar: "Srinagar",
  jammu: "Jammu",
};

function rowKey(issue, index) {
  return `${issue.bookingId ?? ""}-${issue.orderId ?? ""}-${index}`;
}

const ISSUE_TABLE_COL_SPAN = 12;

function formatStepErrors(step) {
  if (!step || typeof step !== "object") return null;
  const lines = [];
  [
    "error_code",
    "error_description",
    "error_reason",
    "error_source",
    "error_step",
  ].forEach((k) => {
    const v = step[k];
    if (v != null && String(v).trim() !== "")
      lines.push(`${k}: ${String(v)}`);
  });
  return lines.length ? lines.join("\n") : null;
}

const LIFECYCLE_MERGED_TOOLTIP =
  "Merged from our DB payment_history and the latest Razorpay payment snapshot. This is not a guaranteed audit of every Razorpay webhook.";

function copyJsonToClipboard(dispatch, text) {
  if (text == null) return;
  navigator.clipboard.writeText(String(text)).then(
    () => {
      dispatch(openSnackbar({ message: "Copied to clipboard." }));
    },
    () => {
      dispatch(
        openSnackbar({
          message: "Could not copy to clipboard.",
          status: snackBarStatus.ERROR,
        })
      );
    }
  );
}

function stringifyPretty(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function hasRazorpayPayloadsObject(payloads) {
  return (
    payloads &&
    typeof payloads === "object" &&
    !Array.isArray(payloads) &&
    Object.keys(payloads).length > 0
  );
}

function RawRazorpayPayloadsSection({ payloads, showPiiWarning }) {
  const dispatch = useDispatch();
  if (!hasRazorpayPayloadsObject(payloads)) return null;

  const copyAll = () => copyJsonToClipboard(dispatch, stringifyPretty(payloads));

  const orderFetch = payloads.order_fetch_payments;
  const listErr = payloads.order_list_fetch_error;
  const payById = payloads.payment_by_id;
  const fetchErrs = payloads.fetch_errors;

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Accordion variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Raw Razorpay JSON</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {showPiiWarning ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Includes PII when redaction is off. Default responses redact
              email, contact, card-like fields.
            </Alert>
          ) : null}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Button
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={copyAll}
            >
              Copy all
            </Button>
          </Box>
          {orderFetch !== undefined && orderFetch !== null ? (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  order_fetch_payments
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() =>
                    copyJsonToClipboard(dispatch, stringifyPretty(orderFetch))
                  }
                >
                  Copy
                </Button>
              </Box>
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  mt: 0.5,
                  p: 1,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  overflow: "auto",
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {stringifyPretty(orderFetch) || "—"}
              </Typography>
            </Box>
          ) : null}
          {listErr !== undefined && listErr !== null ? (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  order_list_fetch_error
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() =>
                    copyJsonToClipboard(dispatch, stringifyPretty(listErr))
                  }
                >
                  Copy
                </Button>
              </Box>
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  mt: 0.5,
                  p: 1,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  overflow: "auto",
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {stringifyPretty(listErr) || "—"}
              </Typography>
            </Box>
          ) : null}
          {payById &&
          typeof payById === "object" &&
          !Array.isArray(payById) &&
          Object.keys(payById).length > 0 ? (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                payment_by_id
              </Typography>
              {Object.entries(payById).map(([k, v]) => (
                <Box key={k} sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                      {k}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() =>
                        copyJsonToClipboard(dispatch, stringifyPretty(v))
                      }
                    >
                      Copy
                    </Button>
                  </Box>
                  <Typography
                    component="pre"
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      p: 1,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      overflow: "auto",
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {stringifyPretty(v) || "—"}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}
          {fetchErrs !== undefined && fetchErrs !== null ? (
            <Box sx={{ mb: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  fetch_errors
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() =>
                    copyJsonToClipboard(dispatch, stringifyPretty(fetchErrs))
                  }
                >
                  Copy
                </Button>
              </Box>
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  mt: 0.5,
                  p: 1,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  overflow: "auto",
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {stringifyPretty(fetchErrs) || "—"}
              </Typography>
            </Box>
          ) : null}
        </AccordionDetails>
      </Accordion>
    </>
  );
}

function ReconciliationIssueDetailBody({ issue, isOrphan, showPiiWarning }) {
  const lifecycleEvents = sortLifecycleEventsAsc(
    issue.booking_payment_lifecycle_events
  );

  return (
    <>
      <Tooltip title={LIFECYCLE_MERGED_TOOLTIP}>
        <Typography
          variant="subtitle2"
          gutterBottom
          component="span"
          sx={{ cursor: "help", display: "inline-block" }}
        >
          Payment lifecycle (merged)
        </Typography>
      </Tooltip>
      {lifecycleEvents.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No lifecycle events.
        </Typography>
      ) : (
        <Box
          component="ul"
          sx={{ mt: 0, pl: 2, listStyle: "none", mb: 2 }}
        >
          {lifecycleEvents.map((ev, i) => {
            const src = ev.source;
            const srcNorm =
              src == null || String(src).trim() === ""
                ? "—"
                : String(src).toLowerCase();
            const detailStr =
              ev.detail != null
                ? typeof ev.detail === "object"
                  ? JSON.stringify(ev.detail)
                  : String(ev.detail)
                : null;
            return (
              <Box component="li" key={i} sx={{ mb: 1.5 }}>
                <Chip
                  size="small"
                  label={srcNorm}
                  sx={{ mr: 1, verticalAlign: "middle" }}
                />
                <Typography variant="body2" component="span">
                  {ev.label ?? "—"}
                </Typography>{" "}
                <Typography variant="caption" color="text.secondary">
                  {formatIsoForDisplay(ev.at)}
                </Typography>
                {detailStr ? (
                  <Tooltip
                    title={
                      detailStr.length > 500
                        ? `${detailStr.slice(0, 500)}…`
                        : detailStr
                    }
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        mt: 0.5,
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {detailStr.length > 160
                        ? `${detailStr.slice(0, 160)}…`
                        : detailStr}
                    </Typography>
                  </Tooltip>
                ) : null}
              </Box>
            );
          })}
        </Box>
      )}
      <Alert severity="info" icon={false} sx={{ mb: 2 }}>
        Gateway timeline steps only include timestamps returned by Razorpay;
        intermediate times may be missing even when status is captured or
        authorized. Full booking-side changes are in booking_payment_history
        (last 20, newest first).
      </Alert>
      <Typography variant="subtitle2" gutterBottom>
        Reasons
      </Typography>
      {Array.isArray(issue.reasons) && issue.reasons.length > 0 ? (
        <Box component="ul" sx={{ mt: 0, pl: 2 }}>
          {issue.reasons.map((r, ri) => (
            <li key={ri}>
              <Typography variant="body2">{r}</Typography>
            </li>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No reasons listed.
        </Typography>
      )}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Gateway timeline (primary payment)
      </Typography>
      {getTimelineSteps(issue).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No timeline steps.
        </Typography>
      ) : (
        <Box component="ul" sx={{ mt: 0, pl: 2, listStyle: "none" }}>
          {getTimelineSteps(issue).map((step, si) => {
            const errTxt = formatStepErrors(step);
            const atLabel = formatIsoForDisplay(step.at);
            return (
              <Box component="li" key={si} sx={{ mb: 1.5 }}>
                <Typography variant="body2">
                  <strong>{formatTimelineEventLabel(step.event)}</strong>:{" "}
                  {atLabel}
                </Typography>
                {errTxt ? (
                  <Typography
                    variant="caption"
                    component="div"
                    color="error"
                    sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}
                  >
                    {errTxt}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Box>
      )}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Booking payment history (newest first)
      </Typography>
      <Box sx={{ opacity: isOrphan ? 0.55 : 1 }}>
        {isOrphan ||
        !Array.isArray(issue.booking_payment_history) ||
        issue.booking_payment_history.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {isOrphan
              ? "No booking — payment history not applicable for this row."
              : "No booking payment history."}
          </Typography>
        ) : (
          <Table size="small" sx={{ maxWidth: 960 }}>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Change</TableCell>
                <TableCell>Amounts</TableCell>
                <TableCell>Remaining</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {issue.booking_payment_history.map((h, hi) => (
                <TableRow key={hi}>
                  <TableCell>{formatIsoForDisplay(h.timestamp)}</TableCell>
                  <TableCell>
                    {h.from_status ?? "—"} → {h.to_status ?? "—"}
                  </TableCell>
                  <TableCell>
                    {formatInrFromMinor(h.from_amount)} →{" "}
                    {formatInrFromMinor(h.to_amount)}
                  </TableCell>
                  <TableCell>
                    {formatInrFromMinor(h.from_remaining)} →{" "}
                    {formatInrFromMinor(h.to_remaining)}
                  </TableCell>
                  <TableCell>{h.changed_by ?? "—"}</TableCell>
                  <TableCell>{h.reason ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
        Payments on order
      </Typography>
      {Array.isArray(issue.providerPaymentsSummary) &&
      issue.providerPaymentsSummary.length > 0 ? (
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell>Id</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Captured</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Authorized</TableCell>
              <TableCell>Captured at</TableCell>
              <TableCell>Errors</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {issue.providerPaymentsSummary.map((p, pi) => {
              const norm =
                p.normalizedStatus ?? p.status ?? "—";
              const raw = p.rawStatus;
              const hasRaw = raw != null && String(raw).trim() !== "";
              const errParts = [
                p.error_code,
                p.error_description,
                p.error_reason,
                p.error_source,
                p.error_step,
              ].filter((x) => x != null && String(x).trim() !== "");
              return (
                <TableRow key={pi}>
                  <TableCell>
                    {p.providerPaymentId ?? p.id ?? "—"}
                  </TableCell>
                  <TableCell>{p.method ?? "—"}</TableCell>
                  <TableCell>
                    {hasRaw ? (
                      <Tooltip title={`Raw status: ${raw}`}>
                        <span>{norm}</span>
                      </Tooltip>
                    ) : (
                      norm
                    )}
                  </TableCell>
                  <TableCell>
                    {p.captured === true
                      ? "Yes"
                      : p.captured === false
                      ? "No"
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {formatInrFromMinor(p.amountMinor ?? p.amount)}
                  </TableCell>
                  <TableCell>
                    {formatIsoForDisplay(p.created_at)}
                  </TableCell>
                  <TableCell>
                    {formatIsoForDisplay(p.authorized_at)}
                  </TableCell>
                  <TableCell>
                    {formatIsoForDisplay(p.captured_at)}
                  </TableCell>
                  <TableCell>
                    {errParts.length ? (
                      <Typography
                        variant="caption"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {errParts.join(" · ")}
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No provider payment rows.
        </Typography>
      )}
      <RawRazorpayPayloadsSection
        payloads={issue.razorpay_api_payloads}
        showPiiWarning={showPiiWarning}
      />
    </>
  );
}

const PaymentReconciliationPage = () => {
  const dispatch = useDispatch();
  const allowedLocations = useMemo(() => getAllowedLocations(), []);
  const scanResult = useSelector(selectPaymentReconciliationScan);
  const pageError = useSelector(selectPaymentReconciliationError);
  const bookingDetail = useSelector(selectPaymentReconciliationBookingDetail);
  const bookingDetailError = useSelector(
    selectPaymentReconciliationBookingDetailError
  );

  const loadingReport = useSelector((s) =>
    isApiPending(s, "paymentReconciliationReport")
  );
  const loadingRun = useSelector((s) =>
    isApiPending(s, "paymentReconciliationRun")
  );
  const loadingDetail = useSelector((s) =>
    isApiPending(s, "paymentReconciliationBookingDetail")
  );
  const loading = loadingReport || loadingRun;

  const [limit, setLimit] = useState(100);
  const [createdAfter, setCreatedAfter] = useState(() => dayjs().subtract(30, "day"));
  const [location, setLocation] = useState(
    () => allowedLocations[0] || ""
  );
  const [bookingStatuses, setBookingStatuses] = useState(["IN_PROGRESS"]);
  const [includePaymentLedOrphans, setIncludePaymentLedOrphans] =
    useState(false);
  const [paymentsFromDays, setPaymentsFromDays] = useState(7);
  const [paymentsListMax, setPaymentsListMax] = useState(200);
  const [remediateDialogOpen, setRemediateDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [includeRazorpayApiPayloads, setIncludeRazorpayApiPayloads] =
    useState(false);
  const [redactRazorpayPayloads, setRedactRazorpayPayloads] = useState(true);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailBookingId, setDetailBookingId] = useState(null);

  const filterPayload = useMemo(
    () => ({
      limit,
      created_after: createdAfter,
      location: location || undefined,
      booking_statuses: bookingStatuses.length
        ? bookingStatuses.join(",")
        : undefined,
      include_payment_led_orphans: includePaymentLedOrphans,
      payments_from_days: paymentsFromDays,
      payments_list_max: paymentsListMax,
      include_razorpay_api_payloads: includeRazorpayApiPayloads,
      redact_razorpay_payloads: redactRazorpayPayloads,
    }),
    [
      limit,
      createdAfter,
      location,
      bookingStatuses,
      includePaymentLedOrphans,
      paymentsFromDays,
      paymentsListMax,
      includeRazorpayApiPayloads,
      redactRazorpayPayloads,
    ]
  );

  const handleRunReport = useCallback(() => {
    dispatch(clearPaymentReconciliationError());
    dispatch(fetchPaymentReconciliationReport(filterPayload));
  }, [dispatch, filterPayload]);

  const handleRunRemediate = useCallback(() => {
    setRemediateDialogOpen(false);
    dispatch(clearPaymentReconciliationError());
    dispatch(runPaymentReconciliation(filterPayload));
  }, [dispatch, filterPayload]);

  const toggleExpand = useCallback((key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const copyOrderId = useCallback(
    (orderId) => {
      if (!orderId) return;
      const text = String(orderId);
      navigator.clipboard.writeText(text).then(
        () => {
          dispatch(
            openSnackbar({
              message: "Order id copied for Razorpay dashboard search.",
            })
          );
        },
        () => {
          dispatch(
            openSnackbar({
              message: "Could not copy to clipboard.",
              status: snackBarStatus.ERROR,
            })
          );
        }
      );
    },
    [dispatch]
  );

  const openBookingDetail = useCallback(
    (bookingId) => {
      const id = bookingId != null ? String(bookingId).trim() : "";
      if (!id) return;
      dispatch(clearPaymentReconciliationBookingDetail());
      setDetailBookingId(id);
      setDetailModalOpen(true);
      dispatch(
        fetchPaymentReconciliationBookingDetail({
          bookingId: id,
          include_razorpay_api_payloads: includeRazorpayApiPayloads,
          redact_razorpay_payloads: redactRazorpayPayloads,
        })
      );
    },
    [
      dispatch,
      includeRazorpayApiPayloads,
      redactRazorpayPayloads,
    ]
  );

  const closeBookingDetail = useCallback(() => {
    setDetailModalOpen(false);
    setDetailBookingId(null);
    dispatch(clearPaymentReconciliationBookingDetail());
  }, [dispatch]);

  const issues = scanResult?.issues ?? [];
  const hasIssues = issues.length > 0;
  const summary = scanResult?.summary;
  const remediatedIds = scanResult?.remediated_booking_ids ?? [];
  const remediationErrors = scanResult?.remediation_errors ?? [];
  const showRemediationSummary =
    remediatedIds.length > 0 || remediationErrors.length > 0;

  const summarySeverity = hasIssues ? "warning" : "info";
  const cardsVariant = hasIssues ? "outlined" : "elevation";
  const scanMeta = scanResult?.scan_meta;
  const paymentLedOn = Boolean(scanMeta?.payment_led_enabled);

  if (!isAuthorized("SUPER_ADMIN")) {
    return <Navigate to="/404" replace />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className={styles.page}>
        <Typography variant="h4" component="h1" className={styles.title}>
          Payment reconciliation
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          Compare gateway payments with booking records. Remediation runs the
          same server-side payment sync used when checking a booking.
        </Typography>

        {allowedLocations.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No locations are assigned to your account. You need admin access
            with at least one location to run reconciliation.
          </Alert>
        )}

        {pageError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => dispatch(clearPaymentReconciliationError())}
          >
            {pageError}
          </Alert>
        )}

        <Card variant="outlined" className={styles.filters}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Limit"
                  type="number"
                  fullWidth
                  size="small"
                  value={limit}
                  onChange={(e) =>
                    setLimit(
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Created after"
                  value={createdAfter}
                  onChange={(v) => v && setCreatedAfter(v)}
                  slotProps={{
                    textField: { fullWidth: true, size: "small" },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Location"
                  fullWidth
                  size="small"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  disabled={allowedLocations.length === 0}
                >
                  {allowedLocations.map((loc) => (
                    <MenuItem key={loc} value={loc}>
                      {locationDisplay[loc] || loc}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="booking-statuses-label">
                    Booking statuses
                  </InputLabel>
                  <Select
                    labelId="booking-statuses-label"
                    multiple
                    value={bookingStatuses}
                    onChange={(e) => setBookingStatuses(e.target.value)}
                    input={<OutlinedInput label="Booking statuses" />}
                    renderValue={(selected) => selected.join(", ")}
                  >
                    {BOOKING_STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Default is IN_PROGRESS. Multi-select; sent as comma-separated
                  to the API.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includePaymentLedOrphans}
                      onChange={(e) =>
                        setIncludePaymentLedOrphans(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Include payment-led orphan scan (Razorpay payments without a matching booking)"
                />
                {includePaymentLedOrphans && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Results combine booking-led rows with orphan payment rows.
                    Empty booking id is valid for payments that have no booking
                    with that order id.
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeRazorpayApiPayloads}
                      onChange={(e) =>
                        setIncludeRazorpayApiPayloads(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Include Razorpay API payloads"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={redactRazorpayPayloads}
                      onChange={(e) =>
                        setRedactRazorpayPayloads(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Redact Razorpay payloads"
                />
                {!redactRazorpayPayloads ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Full raw JSON may include PII. Only disable redaction in a
                    secure environment when you need unmasked fields for
                    debugging.
                  </Alert>
                ) : null}
              </Grid>
              {includePaymentLedOrphans && (
                <>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Payments from (days)"
                      type="number"
                      fullWidth
                      size="small"
                      value={paymentsFromDays}
                      onChange={(e) =>
                        setPaymentsFromDays(
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                      inputProps={{ min: 1, max: 90 }}
                      helperText="1–90, default 7"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Payments list max"
                      type="number"
                      fullWidth
                      size="small"
                      value={paymentsListMax}
                      onChange={(e) =>
                        setPaymentsListMax(
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                      inputProps={{ min: 1, max: 500 }}
                      helperText="1–500, default 200"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        <div className={styles.actions}>
          <Button
            variant="contained"
            onClick={handleRunReport}
            disabled={loading || !location}
          >
            Run report
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => setRemediateDialogOpen(true)}
            disabled={loading || !location}
          >
            Run &amp; remediate
          </Button>
        </div>

        {showRemediationSummary && scanResult && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Last remediation result
            </Typography>
            {remediatedIds.length > 0 && (
              <Typography variant="body2" component="div">
                Updated bookings: {remediatedIds.join(", ")}
              </Typography>
            )}
            {remediationErrors.length > 0 && (
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                {remediationErrors.map((err, i) => {
                  const bid = err.booking_id ?? err.bookingId;
                  return (
                    <li key={`${bid}-${i}`}>
                      <Typography variant="body2">
                        {bid}: {err.error}
                      </Typography>
                    </li>
                  );
                })}
              </Box>
            )}
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={handleRunReport}
              disabled={loading}
            >
              Run report again
            </Button>
          </Alert>
        )}

        {paymentLedOn && scanResult && !loading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Payment-led scan
            </Typography>
            <Typography variant="body2" component="div">
              Payments fetched:{" "}
              {scanMeta.payment_led_payments_fetched ?? "—"}
            </Typography>
            {(() => {
              const apiErr = scanMeta.payment_led_api_errors;
              const show =
                apiErr != null &&
                apiErr !== "" &&
                !(Array.isArray(apiErr) && apiErr.length === 0) &&
                !(typeof apiErr === "object" &&
                  !Array.isArray(apiErr) &&
                  Object.keys(apiErr).length === 0);
              if (!show) return null;
              return (
                <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                  API errors:{" "}
                  {typeof apiErr === "object"
                    ? JSON.stringify(apiErr)
                    : String(apiErr)}
                </Typography>
              );
            })()}
          </Alert>
        )}

        <Grid container spacing={2} className={styles.cards}>
          {loading ? (
            <>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rounded" height={88} />
                </Grid>
              ))}
            </>
          ) : (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant={cardsVariant}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Scanned
                    </Typography>
                    <Typography variant="h5">
                      {scanResult?.scanned_count ?? "—"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant={cardsVariant}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Missing provider payment id
                    </Typography>
                    <Typography variant="h5">
                      {summary?.missing_provider_payment_id ?? "—"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant={cardsVariant}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Status drift
                    </Typography>
                    <Typography variant="h5">
                      {summary?.status_drift ?? "—"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant={cardsVariant}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Transaction id mismatch
                    </Typography>
                    <Typography variant="h5">
                      {summary?.transaction_id_mismatch ?? "—"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant={cardsVariant}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      DB paid, gateway not authorized/captured
                    </Typography>
                    <Typography variant="h5">
                      {summary?.db_paid_gateway_not_authorized_or_captured ??
                        "—"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant={cardsVariant}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Razorpay payment, no matching booking
                    </Typography>
                    <Typography variant="h5">
                      {summary?.razorpay_payment_no_matching_booking ?? "—"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
        </Grid>

        {hasIssues && scanResult && !loading && (
          <Alert severity={summarySeverity} sx={{ mb: 2 }}>
            This scan found {issues.length} mismatch
            {issues.length === 1 ? "" : "es"}. Review the table below.
          </Alert>
        )}

        {scanResult && !loading && !hasIssues && (
          <Alert severity="success" sx={{ mb: 2 }}>
            No mismatches in this scan.
          </Alert>
        )}

        <Typography variant="h6" gutterBottom>
          Issues
        </Typography>
        <TableContainer
          className={styles.tableWrap}
          component={Card}
          variant="outlined"
          sx={{ overflowX: "auto" }}
        >
          {loading ? (
            <Box sx={{ p: 2 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" height={40} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Booking</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Booking created</TableCell>
                  <TableCell>Slot</TableCell>
                  <TableCell>Primary gateway</TableCell>
                  <TableCell>Razorpay status</TableCell>
                  <TableCell>DB booking status</TableCell>
                  <TableCell>DB transaction</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Suggested provider payment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={ISSUE_TABLE_COL_SPAN}>
                      <Typography variant="body2" color="text.secondary">
                        {scanResult
                          ? "No rows — scan returned zero issues."
                          : "Run a report to load issues."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  issues.map((issue, index) => {
                    const rk = rowKey(issue, index);
                    const isOpen = expanded.has(rk);
                    const bookingId = issue.bookingId ?? issue.booking_id;
                    const loc = issue.location ?? "";
                    const isOrphan = isReconciliationOrphanIssue(issue);
                    const primaryGw = getPrimaryGatewayStatusDisplay(issue);
                    const primaryGwTip = getPrimaryGatewayStatusTooltip(issue);
                    const recordsLink =
                      bookingId && loc
                        ? `/booking-records?booking_id=${encodeURIComponent(
                            bookingId
                          )}&location=${encodeURIComponent(loc)}`
                        : bookingId
                        ? `/booking-records?booking_id=${encodeURIComponent(
                            bookingId
                          )}`
                        : null;
                    const rzDisplay = getRazorpayPaymentStatusDisplay(issue);
                    const rzTooltipParts = [];
                    if (issue.razorpay_primary_payment_id)
                      rzTooltipParts.push(
                        `Primary payment id: ${issue.razorpay_primary_payment_id}`
                      );
                    const rzCell = (
                      <span>
                        {rzTooltipParts.length ? (
                          <Tooltip title={rzTooltipParts.join("\n")}>
                            <span>{rzDisplay}</span>
                          </Tooltip>
                        ) : (
                          rzDisplay
                        )}
                      </span>
                    );
                    return (
                      <React.Fragment key={rk}>
                        <TableRow hover>
                          <TableCell padding="checkbox">
                            <IconButton
                              size="small"
                              aria-expanded={isOpen}
                              aria-label={
                                isOpen ? "Collapse details" : "Expand details"
                              }
                              onClick={() => toggleExpand(rk)}
                            >
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              {isOrphan ? (
                                <Chip
                                  size="small"
                                  label="No booking"
                                  color="default"
                                  variant="outlined"
                                />
                              ) : null}
                              {recordsLink ? (
                                <Link
                                  to={recordsLink}
                                  className={styles.bookingLink}
                                >
                                  {bookingId}
                                </Link>
                              ) : (
                                <Typography
                                  variant="body2"
                                  component="span"
                                  color={
                                    isOrphan ? "text.secondary" : "inherit"
                                  }
                                >
                                  {bookingId ?? "—"}
                                </Typography>
                              )}
                              {bookingId &&
                              String(bookingId).trim() !== "" ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => openBookingDetail(bookingId)}
                                >
                                  View detail
                                </Button>
                              ) : null}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Typography variant="body2" component="span">
                                {issue.orderId ?? "—"}
                              </Typography>
                              {issue.orderId ? (
                                <Tooltip title="Copy order id (Razorpay search)">
                                  <IconButton
                                    size="small"
                                    onClick={() => copyOrderId(issue.orderId)}
                                    aria-label="Copy order id"
                                  >
                                    <ContentCopyIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </Box>
                          </TableCell>
                          <TableCell>{loc || "—"}</TableCell>
                          <TableCell>
                            {formatIsoForDisplay(issue.booking_created_at)}
                          </TableCell>
                          <TableCell>{formatSlotDisplay(issue)}</TableCell>
                          <TableCell>
                            {primaryGwTip ? (
                              <Tooltip title={primaryGwTip}>
                                <span>{primaryGw}</span>
                              </Tooltip>
                            ) : (
                              primaryGw
                            )}
                          </TableCell>
                          <TableCell>{rzCell}</TableCell>
                          <TableCell>{issue.dbBookingStatus ?? "—"}</TableCell>
                          <TableCell>{issue.dbTransactionId ?? "—"}</TableCell>
                          <TableCell>
                            {getIssueKindLabel(issue.issueKind)}
                          </TableCell>
                          <TableCell>
                            {issue.suggestedProviderPaymentId ?? "—"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={ISSUE_TABLE_COL_SPAN}
                            sx={{
                              py: 0,
                              borderBottom: isOpen ? undefined : 0,
                            }}
                          >
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 1 }}>
                                <ReconciliationIssueDetailBody
                                  issue={issue}
                                  isOrphan={isOrphan}
                                  showPiiWarning={!redactRazorpayPayloads}
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <Dialog
          open={remediateDialogOpen}
          onClose={() => setRemediateDialogOpen(false)}
          aria-labelledby="remediate-dialog-title"
          aria-describedby="remediate-dialog-description"
        >
          <DialogTitle id="remediate-dialog-title">
            Run and remediate?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="remediate-dialog-description">
              This will update bookings on the server using the payment sync
              pipeline (same logic as checking a booking against the gateway).
              Only continue if you understand that database booking and payment
              fields may change.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemediateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRunRemediate}
              color="warning"
              variant="contained"
              autoFocus
            >
              Confirm remediation
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={detailModalOpen}
          onClose={closeBookingDetail}
          maxWidth="lg"
          fullWidth
          scroll="paper"
          aria-labelledby="booking-detail-dialog-title"
        >
          <DialogTitle id="booking-detail-dialog-title">
            Booking {detailBookingId ?? ""}
          </DialogTitle>
          <DialogContent dividers>
            {loadingDetail ? (
              <Skeleton variant="rounded" height={280} />
            ) : bookingDetailError ? (
              <Alert severity="error">{bookingDetailError}</Alert>
            ) : bookingDetail && !bookingDetail.booking_found ? (
              <Typography variant="body1">Booking not found</Typography>
            ) : bookingDetail && bookingDetail.booking_found ? (
              bookingDetail.issues.length === 0 ? (
                <Typography color="text.secondary">
                  No issues for this booking.
                </Typography>
              ) : (
                bookingDetail.issues.map((detailIssue, idx) => (
                  <Box
                    key={`${detailBookingId}-${idx}`}
                    sx={{ mb: idx < bookingDetail.issues.length - 1 ? 3 : 0 }}
                  >
                    {idx > 0 ? <Divider sx={{ mb: 2 }} /> : null}
                    <ReconciliationIssueDetailBody
                      issue={detailIssue}
                      isOrphan={isReconciliationOrphanIssue(detailIssue)}
                      showPiiWarning={!redactRazorpayPayloads}
                    />
                  </Box>
                ))
              )
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeBookingDetail}>Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </LocalizationProvider>
  );
};

export default PaymentReconciliationPage;
